import { ChangeEventHandler, useEffect, useState } from "react"
import { useController, UseFormReturn } from "react-hook-form"
import { mediaQuery } from "shared/styles/mixins"
import styled from "styled-components"

import {
  NumberInputButton,
  NumberInputElement,
} from "./styles/numberInputStyles"

const FormInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  grid-column: span 2;
`

const InputWrapper = styled.div`
  display: flex;
  height: ${(props) => props.theme.input.height};

  ${mediaQuery.mobileDevice} {
    height: ${(props) => props.theme.mobile.input.height};
  }
`

const Label = styled.label`
  margin-right: 10px;
  color: ${(props) => props.theme.input.textColor};
  size: ${(props) => props.theme.input.textSize};
`

export interface NumberInputProps {
  label?: string | JSX.Element
  min?: number
  max?: number
  name: string
  defaultValue?: number
  formHook: UseFormReturn<any>
  disabled?: boolean
  required?: boolean
}

const NumberInput = ({
  min = 1,
  max = 1000,
  name,
  label,
  formHook,
  required = false,
  disabled = false,
  defaultValue = 1,
}: NumberInputProps) => {
  const { setValue, control } = formHook
  const id = `${name}-number`

  const {
    field: { value },
  } = useController({
    name,
    defaultValue,
    control,
    rules: { required },
  })

  // InputValue is not directly tied to quantity so that the user
  // can manually input values, including clearing the input altogether during
  // input. And then when the input is blurred we will re-sync values and
  // ensure min/max are adhered to.
  const [inputValue, setInputValue] = useState<number | undefined>(value)
  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setInputValue(
      event.target.value !== "" ? Number(event.target.value) : undefined,
    )
  }

  const handleBlur = () => {
    const newValue = Math.max(Math.min(max, inputValue ?? 0), min)
    setInputValue(newValue)
    setValue(name, newValue)
  }

  const increment = () => {
    if (value >= max) {
      return
    }
    setValue(name, value + 1)
  }

  const decrement = () => {
    if (value <= min) {
      return
    }
    setValue(name, value - 1)
  }

  return (
    <FormInput>
      <Label htmlFor={id}>{label}:</Label>
      <InputWrapper>
        <NumberInputButton
          onClick={decrement}
          disabled={disabled}
          aria-label="Decrement"
        >
          -
        </NumberInputButton>
        <NumberInputElement
          id={id}
          name={name}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
        />
        <NumberInputButton
          onClick={increment}
          disabled={disabled}
          aria-label="Increment"
        >
          +
        </NumberInputButton>
      </InputWrapper>
    </FormInput>
  )
}

export default NumberInput
